import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Plus, RefreshCw, X } from 'lucide-react';
import { entryMacros, foodPortion, isMassUnit, offToFood } from '../food';
import { FONT_MONO } from '../theme';
import { Button, NumberInput } from '../ui';

const DARK_INPUT = { background: '#161616', color: '#fff', border: '1px solid #2a2a2a' };

/* Full-screen camera barcode scanner (ZXing). A code is looked up in Mo's
 * own foods first (`index`, from buildBarcodeIndex: no network, his usual
 * amount), then via the backend Open Food Facts proxy. Either way a hit
 * shows a confirm row with the amount instead of logging at once, so a code
 * the camera keeps seeing can't log twice. An unknown code offers the label
 * photo instead (onReadLabel), which then stores the code on that food. */
export function BarcodeScanner({ t, token, index, onLog, onReadLabel, onClose }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const busyRef = useRef(false);
  const lastRef = useRef({ code: null, at: 0 });
  const addedRef = useRef({ code: null, at: 0 });
  // The camera callback is bound once at mount, so everything it reads
  // that changes later goes through a ref.
  const indexRef = useRef(index);
  indexRef.current = index;
  const pendingRef = useRef(null);
  const [pending, setPendingState] = useState(null); // { food, amount, usual, code }
  const setPending = (p) => { pendingRef.current = p; setPendingState(p); };
  const [unknown, setUnknown] = useState(null); // a code nobody knows
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState('starting'); // starting | scanning | error
  const [banner, setBanner] = useState(null); // { ok, text }
  const [manual, setManual] = useState('');
  const [longScan, setLongScan] = useState(false); // true after ~7s without a hit
  const [torch, setTorch] = useState({ supported: false, on: false });

  const fetchCode = async (clean) => {
    const res = await fetch(`/api/food/barcode/${encodeURIComponent(clean)}`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json().catch(() => ({}));
    return { res, data };
  };

  /* typed: digits entered by hand, which replace an open confirm row and
   * skip the camera de-dupe. */
  const lookup = async (code, typed = false) => {
    const clean = String(code || '').trim();
    if (!clean) return;
    const now = Date.now();
    if (busyRef.current) return;
    if (!typed) {
      if (pendingRef.current) return; // finish the open confirm row first
      if (lastRef.current.code === clean && now - lastRef.current.at < 3500) return;
      // The product just added is usually still in view.
      if (addedRef.current.code === clean && now - addedRef.current.at < 8000) return;
    }
    lastRef.current = { code: clean, at: now };
    setPending(null);
    setUnknown(null);
    const hit = indexRef.current && indexRef.current.get(clean);
    if (hit) {
      setBanner(null);
      setPending({ food: hit.food, amount: hit.usualAmount, usualAmount: hit.usualAmount, usual: true, code: clean });
      return;
    }
    busyRef.current = true;
    setBanner({ ok: null, text: `Looking up ${clean}…` });
    try {
      const { res, data } = await fetchCode(clean);
      if (data.success && data.food) {
        const food = offToFood(data.food);
        setBanner(null);
        setPending({ food, amount: food.portion, usual: false, code: clean });
      } else if (res.status === 404) {
        setBanner(null);
        setUnknown(clean);
      } else {
        // A server-side failure is not the same as an unknown product, and
        // saying "not found" for one sent people hunting for the wrong
        // problem. Clearing the de-dupe lets a steady camera retry at once.
        lastRef.current = { code: null, at: 0 };
        setBanner({ ok: false, text: data.message || 'Lookup failed — try again' });
      }
    } catch {
      lastRef.current = { code: null, at: 0 };
      setBanner({ ok: false, text: 'Lookup failed — check connection' });
    } finally {
      busyRef.current = false;
    }
  };

  const add = () => {
    const p = pendingRef.current;
    if (!p) return;
    const amount = Number(p.amount) > 0 ? Number(p.amount) : foodPortion(p.food);
    onLog({ ...p.food, portion: amount, code: p.code });
    addedRef.current = { code: p.code, at: Date.now() };
    setPending(null);
    setBanner({ ok: true, text: `✓ Added ${p.food.name} · ${amount} ${isMassUnit(p.food.unit) ? p.food.unit : '×'}` });
  };

  const dismiss = () => {
    const code = pendingRef.current?.code || unknown;
    lastRef.current = { code, at: Date.now() };
    setPending(null);
    setUnknown(null);
  };

  /* Saved macros can be stale when Open Food Facts corrected a product.
   * Only `per` is refreshed: the name stays, so it remains the same food in
   * the library, and the amount stays Mo's. */
  const refresh = async () => {
    const p = pendingRef.current;
    if (!p || busyRef.current) return;
    busyRef.current = true;
    setRefreshing(true);
    try {
      const { data } = await fetchCode(p.code);
      if (data.success && data.food && pendingRef.current?.code === p.code) {
        setPending({ ...pendingRef.current, food: { ...p.food, per: offToFood(data.food).per }, refreshed: true });
        setBanner({ ok: true, text: 'Macros updated from the food database' });
      } else {
        setBanner({ ok: false, text: 'Not found online. Keeping your saved macros.' });
      }
    } catch {
      setBanner({ ok: false, text: 'Lookup failed. Keeping your saved macros.' });
    } finally {
      busyRef.current = false;
      setRefreshing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [mod, zx] = await Promise.all([import('@zxing/browser'), import('@zxing/library')]);
        // Product barcodes only + TRY_HARDER → much better on imperfect frames.
        const hints = new Map();
        hints.set(zx.DecodeHintType.POSSIBLE_FORMATS, [
          zx.BarcodeFormat.EAN_13, zx.BarcodeFormat.EAN_8,
          zx.BarcodeFormat.UPC_A, zx.BarcodeFormat.UPC_E,
          zx.BarcodeFormat.CODE_128,
        ]);
        hints.set(zx.DecodeHintType.TRY_HARDER, true);
        const reader = new mod.BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 150 });
        if (cancelled) return;
        setStatus('scanning');
        controlsRef.current = await reader.decodeFromConstraints(
          {
            video: {
              facingMode: { ideal: 'environment' },
              width: { ideal: 1920 }, height: { ideal: 1080 },
              // Ask for continuous autofocus where the browser supports it
              // (unknown constraints are ignored per spec — safe everywhere).
              advanced: [{ focusMode: 'continuous' }],
            },
          },
          videoRef.current,
          (result) => { if (result) lookup(result.getText()); },
        );
        // Torch (flashlight) support — offer a toggle when the camera has one.
        try {
          const track = videoRef.current?.srcObject?.getVideoTracks?.()[0];
          if (track?.getCapabilities?.().torch) setTorch({ supported: true, on: false });
        } catch {}
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    const longTimer = window.setTimeout(() => { if (!cancelled) setLongScan(true); }, 7000);
    return () => {
      cancelled = true;
      window.clearTimeout(longTimer);
      try { controlsRef.current?.stop(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTorch = async () => {
    try {
      const track = videoRef.current?.srcObject?.getVideoTracks?.()[0];
      if (!track) return;
      const next = !torch.on;
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorch(prev => ({ ...prev, on: next }));
    } catch {}
  };

  const bannerColor = banner?.ok === true ? t.accent : banner?.ok === false ? t.danger : t.textMuted;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: '#000', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '14px 18px', paddingTop: 'max(14px, env(safe-area-inset-top))', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#fff' }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>Scan barcode</span>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer', color: '#fff', display: 'flex' }}><X size={18} /></button>
      </div>

      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <video ref={videoRef} autoPlay muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        {/* Scan frame + live feedback */}
        <style>{`
          @keyframes bcSweep { 0% { top: 6%; } 50% { top: calc(94% - 2px); } 100% { top: 6%; } }
          @keyframes bcPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
        `}</style>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ width: '78%', maxWidth: 320, height: 150, position: 'relative', border: '2px solid rgba(255,255,255,0.85)', borderRadius: 12, boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
            {status === 'scanning' && (
              <div style={{
                position: 'absolute', left: '4%', right: '4%', height: 2, borderRadius: 2,
                background: t.accent, boxShadow: `0 0 12px 2px ${t.accent}`,
                animation: 'bcSweep 2.2s ease-in-out infinite',
              }} />
            )}
          </div>
          {/* Status + tips under the frame */}
          <div style={{ marginTop: 14, textAlign: 'center', padding: '0 24px' }}>
            {status === 'scanning' && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#fff', fontSize: 13, fontWeight: 600 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.accent, animation: 'bcPulse 1.2s ease-in-out infinite' }} />
                Scanning…
              </div>
            )}
            <div style={{ marginTop: 6, fontSize: 12, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
              Hold the barcode 10–15 cm away · fill the frame · keep it steady
            </div>
            {longScan && status === 'scanning' && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#fbbf24', lineHeight: 1.5 }}>
                Still looking… move slightly further back so the camera can focus,
                add more light{torch.supported ? ' (or tap the flashlight)' : ''}, or type the digits below.
              </div>
            )}
          </div>
        </div>

        {/* Torch toggle */}
        {torch.supported && (
          <button onClick={toggleTorch} style={{
            position: 'absolute', top: 12, right: 12,
            background: torch.on ? t.accent : 'rgba(0,0,0,0.55)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.35)', borderRadius: 10,
            padding: '8px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            🔦 {torch.on ? 'On' : 'Light'}
          </button>
        )}
        {status === 'error' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center', color: '#fff', fontSize: 14, lineHeight: 1.5 }}>
            Camera unavailable. Grant camera permission (needs HTTPS), or type the barcode below.
          </div>
        )}
        {banner && (
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 16, display: 'flex', justifyContent: 'center', padding: '0 16px' }}>
            <div style={{ background: 'rgba(0,0,0,0.75)', color: bannerColor, border: `1px solid ${bannerColor}`, borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 600, maxWidth: '100%', textAlign: 'center' }}>
              {banner.text}
            </div>
          </div>
        )}
      </div>

      {pending && (() => {
        const mass = isMassUnit(pending.food.unit);
        const unitText = mass ? pending.food.unit : '×';
        const m = entryMacros({ per: pending.food.per, amount: Number(pending.amount) || 0 });
        return (
          <div style={panelStyle}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ color: t.accent }}>✓</span> {pending.food.name}
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 3 }}>
                  {pending.usual
                    ? <>{foodAmountText(pending)} (your usual) · from your foods</>
                    : <>{pending.food.serving || `${foodPortion(pending.food)} ${unitText}`} · food database</>}
                </div>
              </div>
              <button onClick={dismiss} aria-label="Not this one" title="Not this one" style={closeBtn}><X size={16} /></button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <div style={{ width: 112, flexShrink: 0 }}>
                <NumberInput t={t} value={pending.amount} min={0} step={mass ? 10 : 0.5} suffix={unitText}
                  onChange={v => setPending({ ...pendingRef.current, amount: v })}
                  style={{ ...DARK_INPUT, height: 44, fontSize: 15 }} />
              </div>
              <Button t={t} variant="primary" onClick={add} disabled={!(Number(pending.amount) > 0)}
                style={{ flex: 1, justifyContent: 'center', minHeight: 44, fontSize: 14 }}>
                <Plus size={15} /> Add · {Math.round(m.kcal)} kcal · {Math.round(m.protein)} P
              </Button>
            </div>
            {pending.usual && !pending.refreshed && (
              <button onClick={refresh} disabled={refreshing} style={linkBtn}>
                <RefreshCw size={12} /> {refreshing ? 'Checking the food database…' : 'Refresh macros from the food database'}
              </button>
            )}
          </div>
        );
      })()}

      {unknown && !pending && (
        <div style={panelStyle}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Not in the food database</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 3, fontFamily: FONT_MONO }}>{unknown}</div>
            </div>
            <button onClick={dismiss} aria-label="Scan again" title="Scan again" style={closeBtn}><X size={16} /></button>
          </div>
          {onReadLabel && (
            <>
              <Button t={t} variant="primary" onClick={() => onReadLabel(unknown)}
                style={{ width: '100%', justifyContent: 'center', minHeight: 44, fontSize: 14, marginTop: 10 }}>
                <ImagePlus size={15} /> Read the label instead
              </Button>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 6, lineHeight: 1.4 }}>
                The barcode is saved with the food, so the next scan finds it.
              </div>
            </>
          )}
        </div>
      )}

      {/* Manual entry fallback */}
      <div style={{ padding: '12px 18px', paddingBottom: 'max(12px, env(safe-area-inset-bottom))', background: '#0a0a0a', display: 'flex', gap: 8 }}>
        <input value={manual} onChange={e => setManual(e.target.value)} inputMode="numeric" placeholder="Enter barcode digits…"
          onKeyDown={e => { if (e.key === 'Enter') { lookup(manual, true); setManual(''); } }}
          style={{ flex: 1, minWidth: 0, ...DARK_INPUT, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: FONT_MONO }} />
        <Button t={t} variant="primary" disabled={!manual.trim()} onClick={() => { lookup(manual, true); setManual(''); }}>Look up</Button>
      </div>
    </div>
  );
}

const panelStyle = {
  padding: '12px 18px', background: '#111', borderTop: '1px solid #262626',
};
const closeBtn = {
  flexShrink: 0, width: 40, height: 40, marginTop: -8, marginRight: -10, background: 'transparent', border: 'none',
  color: 'rgba(255,255,255,0.7)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const linkBtn = {
  marginTop: 8, minHeight: 32, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer',
  color: 'rgba(255,255,255,0.6)', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6,
};

/* '250 g' / '1.5 ×' for the usual amount a confirm row opened with. */
function foodAmountText(p) {
  const unit = isMassUnit(p.food.unit) ? ` ${p.food.unit}` : ' ×';
  return `${p.usualAmount}${unit}`;
}
